import { Button, IconButton, styled, useTheme } from '@mui/material';
import { ReactComponent as ChevronRightIcon } from '../../assets/icons/chevron-right.svg';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { isSmallScreenState } from '../../state/UIState';
import { Typography } from '../Typography/Typography';
import { getMonthLabel } from '../../utils/formatters';
import MonthPicker from '../MonthPicker/MonthPicker';

interface Props {
  selectedYear: number;
  selectedMonth: number;
  handleSelectedMonth: (month: number, year: number) => void;
}

export function MonthlyNavigation({ selectedYear, selectedMonth, handleSelectedMonth }: Props) {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const currentPeriod = useMemo(() => {
    const date = new Date();
    return {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  }, []);

  return (
    <WeekPaginationWrap isSmallScreen={isSmallScreen}>
      <IconButton
        onClick={() => {
          //get previous month and year
          const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
          const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
          handleSelectedMonth(prevMonth, prevYear);
        }}
        sx={{
          svg: { path: { fill: colors.icon.accent, opacity: '1' } },
          backgroundColor: colors.surfaceBackground.highlighted,
          borderRadius: '4px',
          ':hover': {
            backgroundColor: colors.surfaceBackground.highlighted,
          },
        }}
      >
        <StyledPrevIcon />
      </IconButton>
      <IconButton
        sx={{
          svg: {
            path: {
              fill: colors.icon.accent,
              opacity:
                selectedYear === currentPeriod.year && selectedMonth === currentPeriod.month
                  ? '0.3'
                  : '1',
            },
          },
          backgroundColor: colors.surfaceBackground.highlighted,
          borderRadius: '4px',
          ':hover': {
            backgroundColor: colors.surfaceBackground.highlighted,
          },
        }}
        disabled={selectedYear === currentPeriod.year && selectedMonth === currentPeriod.month}
        onClick={() => {
          //get next month and year
          const nextMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
          const nextYear = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
          handleSelectedMonth(nextMonth, nextYear);
        }}
      >
        <StyledNextIcon />
      </IconButton>
      <Typography
        variant='body'
        color={colors.text.main}
        style={{
          lineHeight: 'inherit',
          fontSize: '1rem',
          textWrap: 'nowrap',
          width: '115px',
        }}
      >
        {`${getMonthLabel(selectedMonth)} ${selectedYear}`}
      </Typography>
      <MonthPicker
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={handleSelectedMonth}
        maxDate={new Date(currentPeriod.year, currentPeriod.month - 1)}
      />
      <Button
        disabled={selectedYear === currentPeriod.year && selectedMonth === currentPeriod.month}
        sx={{
          '&.Mui-disabled': {
            color: colors.textAccent.disabled,
          },
          textWrap: 'nowrap',
          width: '150px',
        }}
        onClick={() => {
          handleSelectedMonth(currentPeriod.month, currentPeriod.year);
        }}
      >
        {selectedYear === currentPeriod.year && selectedMonth === currentPeriod.month
          ? 'Current month'
          : 'Go to current month'}
      </Button>
    </WeekPaginationWrap>
  );
}

const WeekPaginationWrap = styled('div')<{ isSmallScreen: boolean }>`
  display: flex;
  justify-content: ${({ isSmallScreen }) => (isSmallScreen ? 'flex-start' : 'center')};
  align-items: center;
  gap: 10px;
  width: ${({ isSmallScreen }) => (isSmallScreen ? '100%' : '40%')};
`;

const StyledPrevIcon = styled(ChevronRightIcon)`
  transform: rotate(180deg);
`;

const StyledNextIcon = styled(ChevronRightIcon)``;
